import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calculator, Landmark, CalendarClock, Settings2, Download, CheckCircle2, AlertTriangle, Clock, Save } from "lucide-react";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const pct = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 3 })} %`;

const Skeleton = ({ rows = 5, testid }) => (
  <div className="space-y-2 animate-pulse" data-testid={testid}>
    <div className="grid sm:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-20 bg-[#E9ECEF]" />)}
    </div>
    {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-9 bg-[#F1F3F5]" />)}
  </div>
);

const KPI = ({ label, value, accent, testid }) => (
  <div className="bg-white border border-[#DEE2E6] p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-bold">{label}</div>
    <div className="font-display text-2xl font-black mt-1" style={{ color: accent || "#0A0A0A" }}>{value}</div>
  </div>
);

export default function ChefPaieFiscal() {
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [urssaf, setUrssaf] = useState(null);
  const [taxes, setTaxes] = useState(null);
  const [ech, setEch] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [declDialog, setDeclDialog] = useState(null);
  const [declForm, setDeclForm] = useState({ montant: "", reference: "", note: "" });

  const loadUrssaf = useCallback(async () => {
    try {
      const { data } = await api.get(`/chef/paie-fiscal/urssaf?annee=${annee}&mois=${mois}`);
      setUrssaf(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [annee, mois, tick]);

  const loadTaxes = useCallback(async () => {
    try {
      const { data } = await api.get(`/chef/paie-fiscal/taxes?annee=${annee}`);
      setTaxes(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [annee, tick]);

  const loadEch = useCallback(async () => {
    try {
      const { data } = await api.get(`/chef/paie-fiscal/echeancier?annee=${annee}`);
      setEch(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [annee, tick]);

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/chef/paie-fiscal/config");
      setCfg(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadCfg(); }, []);
  useEffect(() => { setLoading(true); loadUrssaf().finally(() => setLoading(false)); }, [loadUrssaf]);
  useEffect(() => { loadTaxes(); loadEch(); }, [loadTaxes, loadEch]);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/chef/paie-fiscal/config", {
        effectif: cfg.effectif === "" || cfg.effectif === null ? null : parseInt(cfg.effectif),
        taux_at: parseFloat(cfg.taux_at) || 0,
        taux_versement_mobilite: parseFloat(cfg.taux_versement_mobilite) || 0,
        periodicite_urssaf: cfg.periodicite_urssaf,
        assujetti_tva: !!cfg.assujetti_tva,
        regime_tva: cfg.regime_tva,
        assujetti_is: !!cfg.assujetti_is,
        part_cdd_pct: parseFloat(cfg.part_cdd_pct) || 0,
        notes: cfg.notes || "",
      });
      setCfg({ ...data, effectif_detecte: cfg.effectif_detecte });
      setTick((t) => t + 1);
      toast.success("Paramètres fiscaux enregistrés — calculs recalculés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const closeDecl = () => {
    setDeclDialog(null);
    // Radix laisse parfois `pointer-events:none` sur le body après une fermeture
    // programmatique : on le purge pour ne pas bloquer les clics suivants.
    setTimeout(() => { document.body.style.pointerEvents = ""; }, 250);
  };

  const submitDecl = async () => {
    const target = declDialog;
    try {
      await api.post("/chef/paie-fiscal/declarations", {
        echeance_key: target.key,
        periode: target.periode,
        montant: parseFloat(declForm.montant) || 0,
        reference: declForm.reference,
        note: declForm.note,
      });
      closeDecl();
      setDeclForm({ montant: "", reference: "", note: "" });
      setTick((t) => t + 1);
      toast.success("Échéance marquée comme déclarée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const undoDecl = async (id) => {
    try {
      await api.delete(`/chef/paie-fiscal/declarations/${id}`);
      setTick((t) => t + 1);
      toast.success("Déclaration retirée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/chef/paie-fiscal/export.csv?annee=${annee}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `pag_paie_fiscal_${annee}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
  };

  const years = [now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div data-testid="chef-paie-fiscal-page">
      <PageHeader
        eyebrow="Paie & Fiscal"
        title="Cotisations, taxes & échéances"
        sub="Décomposition URSSAF de votre masse salariale, contributions assises sur les salaires et calendrier des déclarations. Barèmes versionnés appliqués à la date de chaque période — à confirmer avec votre expert-comptable."
        right={
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Année</Label>
              <Select value={String(annee)} onValueChange={(v) => setAnnee(parseInt(v))}>
                <SelectTrigger className="h-10 w-[110px]" data-testid="pf-year"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={exportCsv} data-testid="pf-export-csv" className="h-10">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10">
        <Tabs defaultValue="urssaf">
          <TabsList data-testid="pf-tabs">
            <TabsTrigger value="urssaf" data-testid="pf-tab-urssaf"><Landmark className="h-4 w-4 mr-1" />URSSAF</TabsTrigger>
            <TabsTrigger value="taxes" data-testid="pf-tab-taxes"><Calculator className="h-4 w-4 mr-1" />Taxes</TabsTrigger>
            <TabsTrigger value="echeancier" data-testid="pf-tab-echeancier"><CalendarClock className="h-4 w-4 mr-1" />Échéancier</TabsTrigger>
            <TabsTrigger value="config" data-testid="pf-tab-config"><Settings2 className="h-4 w-4 mr-1" />Réglages</TabsTrigger>
          </TabsList>

          {/* ---------------- URSSAF ---------------- */}
          <TabsContent value="urssaf" className="mt-5" data-testid="pf-panel-urssaf">
            <div className="flex items-end gap-2 mb-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Mois</Label>
                <Select value={String(mois)} onValueChange={(v) => setMois(parseInt(v))}>
                  <SelectTrigger className="h-10 w-[160px]" data-testid="pf-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {urssaf?.echeance && (
                <div className="text-xs text-[#495057] pb-2">
                  Échéance de déclaration : <b className="font-mono">{urssaf.echeance}</b>
                </div>
              )}
            </div>

            {loading && !urssaf && <Skeleton testid="pf-urssaf-skeleton" rows={8} />}
            {urssaf && (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                  <KPI label="Masse brute" value={eur(urssaf.base_brut)} testid="pf-kpi-brut" />
                  <KPI label="Part salariale" value={eur(urssaf.total_salarial)} accent="#F59E0B" testid="pf-kpi-sal" />
                  <KPI label="Part patronale" value={eur(urssaf.total_patronal)} accent="#E60000" testid="pf-kpi-pat" />
                  <KPI label="Total à déclarer" value={eur(urssaf.total_a_declarer)} accent="#002FA7" testid="pf-kpi-total" />
                  <KPI label="Coût employeur" value={eur(urssaf.cout_employeur)} accent="#059669" testid="pf-kpi-cout" />
                </div>

                <div className="text-[11px] text-[#868E96] mb-3" data-testid="pf-source">
                  Source de la masse salariale : <b>{urssaf.masse.source === "bulletins" ? `${urssaf.masse.bulletins_count} bulletin(s) de paie` : "heures de planning × taux horaire"}</b>
                  {" · "}Effectif retenu : <b>{urssaf.effectif}</b>
                  {" · "}Plafond SS période : <b>{eur(urssaf.plafond_periode)}</b>
                </div>
                {urssaf.bareme && (
                  <div className="text-[11px] text-[#495057] mb-3 border border-[#DEE2E6] bg-[#F8F9FA] px-3 py-2 inline-block" data-testid="pf-bareme">
                    Barème réglementaire en vigueur au <b>{new Date(urssaf.bareme.at + "T00:00:00").toLocaleDateString("fr-FR")}</b>
                    {" · "}{urssaf.bareme.rules_count} paramètres · dernière mise à jour le <b>{urssaf.bareme.latest_effective_from ? new Date(urssaf.bareme.latest_effective_from + "T00:00:00").toLocaleDateString("fr-FR") : "—"}</b>
                    {" · "}Source : {urssaf.bareme.sources?.join(" ; ")}
                  </div>
                )}

                {urssaf.lignes.length === 0 ? (
                  <div className="border border-[#DEE2E6] bg-white p-8 text-center text-sm text-[#868E96]" data-testid="pf-urssaf-empty">
                    Aucune masse salariale sur {MONTHS[mois - 1]} {annee}. Déposez des bulletins de paie ou
                    renseignez le planning des salariés pour obtenir le calcul.
                  </div>
                ) : (
                  <div className="border border-[#DEE2E6] bg-white overflow-x-auto" data-testid="pf-urssaf-table">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0A0A0A] text-white">
                        <tr className="text-[10px] uppercase tracking-wider">
                          <th className="text-left p-3">Cotisation</th>
                          <th className="text-left p-3">Organisme</th>
                          <th className="text-right p-3">Base</th>
                          <th className="text-right p-3">Taux sal.</th>
                          <th className="text-right p-3">Taux pat.</th>
                          <th className="text-right p-3">Salariale</th>
                          <th className="text-right p-3">Patronale</th>
                          <th className="text-right p-3">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#DEE2E6]">
                        {urssaf.lignes.map((l, i) => (
                          <tr key={l.key} className={i % 2 ? "bg-[#F8F9FA]" : ""} data-testid={`pf-line-${l.key}`}>
                            <td className="p-3 font-semibold">{l.label}</td>
                            <td className="p-3 text-[11px] text-[#495057]">{l.organisme}</td>
                            <td className="p-3 text-right font-mono text-xs">
                              {eur(l.base_amount)}
                              <div className="text-[9px] text-[#868E96]">{l.base_label}</div>
                            </td>
                            <td className="p-3 text-right font-mono text-xs">{l.taux_salarial ? pct(l.taux_salarial) : "—"}</td>
                            <td className="p-3 text-right font-mono text-xs">{l.taux_patronal ? pct(l.taux_patronal) : "—"}</td>
                            <td className="p-3 text-right font-mono text-xs">{eur(l.montant_salarial)}</td>
                            <td className="p-3 text-right font-mono text-xs">{eur(l.montant_patronal)}</td>
                            <td className="p-3 text-right font-mono text-xs font-bold">{eur(l.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-[#002FA7] text-white font-bold">
                          <td className="p-3" colSpan={5}>Total période</td>
                          <td className="p-3 text-right font-mono">{eur(urssaf.total_salarial)}</td>
                          <td className="p-3 text-right font-mono">{eur(urssaf.total_patronal)}</td>
                          <td className="p-3 text-right font-mono">{eur(urssaf.total_a_declarer)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {urssaf.par_organisme?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="pf-by-organisme">
                    {urssaf.par_organisme.map((o) => (
                      <div key={o.organisme} className="border border-[#DEE2E6] bg-white px-4 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">{o.organisme}</div>
                        <div className="font-display font-black">{eur(o.montant)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ---------------- TAXES ---------------- */}
          <TabsContent value="taxes" className="mt-5" data-testid="pf-panel-taxes">
            {taxes && (
              <>
                <div className="grid sm:grid-cols-3 gap-3 mb-5">
                  <KPI label={`Masse salariale ${annee}`} value={eur(taxes.masse_salariale)} testid="pf-tax-masse" />
                  <KPI label="Dont masse CDD" value={eur(taxes.masse_cdd)} accent="#F59E0B" testid="pf-tax-cdd" />
                  <KPI label="Total taxes dues" value={eur(taxes.total)} accent="#E60000" testid="pf-tax-total" />
                </div>
                <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]" data-testid="pf-taxes-list">
                  {taxes.lignes.map((l) => (
                    <div key={l.key} className={`p-4 flex flex-wrap items-center gap-3 ${l.applicable ? "" : "opacity-55"}`} data-testid={`pf-tax-${l.key}`}>
                      <div className="flex-1 min-w-[220px]">
                        <div className="font-semibold text-sm">{l.label}</div>
                        <div className="text-[11px] text-[#868E96]">{l.organisme}{l.note ? ` · ${l.note}` : ""}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-[#868E96]">Base</div>
                        <div className="font-mono text-xs">{eur(l.base)}</div>
                      </div>
                      <div className="text-right w-16">
                        <div className="text-[10px] uppercase tracking-wider text-[#868E96]">Taux</div>
                        <div className="font-mono text-xs">{l.taux ? pct(l.taux) : "barème"}</div>
                      </div>
                      <div className="text-right w-28">
                        <div className="text-[10px] uppercase tracking-wider text-[#868E96]">Montant</div>
                        <div className="font-display font-black">{l.applicable ? eur(l.montant) : "non due"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------------- ÉCHÉANCIER ---------------- */}
          <TabsContent value="echeancier" className="mt-5" data-testid="pf-panel-echeancier">
            {ech && (
              <>
                <div className="grid sm:grid-cols-4 gap-3 mb-5">
                  <KPI label="Échéances" value={ech.total} testid="pf-ech-total" />
                  <KPI label="Déclarées" value={ech.counts.done || 0} accent="#059669" testid="pf-ech-done" />
                  <KPI label="En retard" value={ech.counts.late || 0} accent="#E60000" testid="pf-ech-late" />
                  <KPI label="À venir" value={ech.counts.upcoming || 0} accent="#002FA7" testid="pf-ech-upcoming" />
                </div>
                <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]" data-testid="pf-ech-list">
                  {ech.items.map((i) => {
                    const c = i.status === "done" ? { color: "#059669", Icon: CheckCircle2, label: "Déclarée" }
                      : i.status === "late" ? { color: "#E60000", Icon: AlertTriangle, label: "En retard" }
                      : { color: "#002FA7", Icon: Clock, label: "À venir" };
                    return (
                      <div key={`${i.key}-${i.periode}`} className="p-3 flex flex-wrap items-center gap-3" data-testid={`pf-ech-${i.key}`}>
                        <c.Icon className="h-4 w-4 shrink-0" style={{ color: c.color }} />
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-sm font-semibold">{i.label}</div>
                          <div className="text-[11px] text-[#868E96]">
                            {i.organisme} · {i.categorie === "social" ? "Social" : "Fiscal"} · période {i.periode}
                          </div>
                        </div>
                        <div className="font-mono text-xs w-24">{i.date}</div>
                        <div className="text-[10px] uppercase tracking-wider font-bold px-2 py-1" style={{ backgroundColor: c.color, color: "white" }}>
                          {c.label}
                        </div>
                        {i.status === "done" ? (
                          <Button size="sm" variant="outline" className="text-[10px] h-7"
                                  data-testid={`pf-ech-undo-${i.key}`} onClick={() => undoDecl(i.declaration.id)}>
                            Annuler
                          </Button>
                        ) : (
                          <Button size="sm" className="text-[10px] h-7 bg-[#059669] text-white"
                                  data-testid={`pf-ech-mark-${i.key}`}
                                  onClick={() => { setDeclDialog(i); setDeclForm({ montant: "", reference: "", note: "" }); }}>
                            Marquer déclarée
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------------- RÉGLAGES ---------------- */}
          <TabsContent value="config" className="mt-5" data-testid="pf-panel-config">
            {cfg && (
              <div className="border border-[#DEE2E6] bg-white p-5 max-w-3xl">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Effectif déclaré (vide = détecté : {cfg.effectif_detecte})</Label>
                    <Input type="number" min="0" value={cfg.effectif ?? ""} data-testid="pf-cfg-effectif"
                           onChange={(e) => setCfg((c) => ({ ...c, effectif: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Taux AT/MP (%)</Label>
                    <Input type="number" step="0.01" value={cfg.taux_at} data-testid="pf-cfg-at"
                           onChange={(e) => setCfg((c) => ({ ...c, taux_at: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Versement mobilité (%)</Label>
                    <Input type="number" step="0.01" value={cfg.taux_versement_mobilite} data-testid="pf-cfg-mobilite"
                           onChange={(e) => setCfg((c) => ({ ...c, taux_versement_mobilite: e.target.value }))} />
                    <div className="text-[10px] text-[#868E96] mt-1">Dû à partir de 11 salariés · taux fixé par votre collectivité.</div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Part de masse salariale en CDD (%)</Label>
                    <Input type="number" step="0.1" value={cfg.part_cdd_pct} data-testid="pf-cfg-cdd"
                           onChange={(e) => setCfg((c) => ({ ...c, part_cdd_pct: e.target.value }))} />
                    <div className="text-[10px] text-[#868E96] mt-1">Utilisé si les contrats ne sont pas renseignés sur les fiches salariés.</div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Périodicité URSSAF</Label>
                    <Select value={cfg.periodicite_urssaf} onValueChange={(v) => setCfg((c) => ({ ...c, periodicite_urssaf: v }))}>
                      <SelectTrigger data-testid="pf-cfg-periodicite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensuelle">Mensuelle</SelectItem>
                        <SelectItem value="trimestrielle">Trimestrielle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Régime TVA</Label>
                    <Select value={cfg.regime_tva} onValueChange={(v) => setCfg((c) => ({ ...c, regime_tva: v }))}>
                      <SelectTrigger data-testid="pf-cfg-tva"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CA3_mensuelle">CA3 mensuelle</SelectItem>
                        <SelectItem value="CA3_trimestrielle">CA3 trimestrielle</SelectItem>
                        <SelectItem value="CA12_annuelle">CA12 annuelle (simplifié)</SelectItem>
                        <SelectItem value="franchise">Franchise en base</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                  {[
                    { key: "assujetti_tva", label: "Assujettie à la TVA" },
                    { key: "assujetti_is", label: "Assujettie à l'impôt sur les sociétés" },
                  ].map((t) => (
                    <button key={t.key} type="button" data-testid={`pf-cfg-${t.key}`}
                            onClick={() => setCfg((c) => ({ ...c, [t.key]: !c[t.key] }))}
                            className={`text-[11px] uppercase tracking-wider font-bold px-3 py-2 border transition ${
                              cfg[t.key] ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"
                            }`}>
                      {cfg[t.key] ? "✓ " : "✗ "}{t.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-[#495057] mt-2">
                  Non assujettie à la TVA ⇒ la taxe sur les salaires devient due (barème 4,25 / 8,50 / 13,60 %).
                </div>

                <div className="mt-4">
                  <Label className="text-[10px] uppercase tracking-wider">Notes internes</Label>
                  <Textarea rows={3} value={cfg.notes || ""} data-testid="pf-cfg-notes"
                            onChange={(e) => setCfg((c) => ({ ...c, notes: e.target.value }))} />
                </div>

                <Button onClick={saveCfg} disabled={saving} className="mt-4 bg-[#002FA7] text-white" data-testid="pf-cfg-save">
                  <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les paramètres"}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!declDialog} onOpenChange={(o) => !o && closeDecl()}>
        <DialogContent data-testid="pf-decl-dialog">
          <DialogHeader><DialogTitle>Marquer comme déclarée</DialogTitle></DialogHeader>
          <div className="text-sm text-[#495057]">{declDialog?.label} — période {declDialog?.periode}</div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Montant déclaré (€)</Label>
              <Input type="number" step="0.01" value={declForm.montant} data-testid="pf-decl-montant"
                     onChange={(e) => setDeclForm({ ...declForm, montant: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Référence / n° de télédéclaration</Label>
              <Input value={declForm.reference} data-testid="pf-decl-ref"
                     onChange={(e) => setDeclForm({ ...declForm, reference: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Note</Label>
              <Textarea rows={2} value={declForm.note} data-testid="pf-decl-note"
                        onChange={(e) => setDeclForm({ ...declForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDecl} data-testid="pf-decl-cancel">Annuler</Button>
            <Button onClick={submitDecl} className="bg-[#059669] text-white" data-testid="pf-decl-submit">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
